/**
 * Ambient lookup for the two well-known PersonType ids used across the SPA.
 *
 * Preferred usage from React components is to read them off
 * `useStores().domainStore.personTypesDomainStore` so re-renders react to
 * the fetched list. This helper exists for the non-React consumers (model
 * classes, static helpers) that used to import the hardcoded string
 * constants directly.
 *
 * IDs are `string` end-to-end because they are 64-bit numeric ids that
 * exceed `Number.MAX_SAFE_INTEGER`.
 *
 * `rootStore` is required lazily (inside the getters) rather than imported
 * at the top of the file to avoid an import cycle: several models on the
 * `rootStore.domainStore` graph (e.g. `EventDetails`) import this helper,
 * and a top-level `import` here would materialise the RootStore before
 * those models finish initialising.
 */
type PersonTypesDomainStoreLike = {
  independentPersonTypeId: string;
  servicedPersonTypeId: string;
};

const getStore = (): PersonTypesDomainStoreLike => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const {rootStore} = require("../stores/rootStore");
  return rootStore.domainStore.personTypesDomainStore;
};

export const personTypeIds = {
  get independent(): string {
    return getStore().independentPersonTypeId;
  },
  get serviced(): string {
    return getStore().servicedPersonTypeId;
  }
};

export const isIndependentPersonType = (id: string): boolean =>
  id === personTypeIds.independent;

export const isServicedPersonType = (id: string): boolean =>
  id === personTypeIds.serviced;
